// Shared marketing nav used across landing, pricing, and blog routes.
//
// Above 960px: full nav (links + Book a demo + Get started).
// Below 960px: hamburger button that opens a drop-down with the same items.
//
// Both CTAs are sitewide constants:
//   - Book a demo  → Calendly (new tab)
//   - Get started  → app.scribenote.com/dashboard?companion (new tab)
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { Wordmark } from "./Wordmark";

type MarketingNavProps = {
  current?: "home" | "use-cases" | "blog" | "pricing";
};

const CALENDLY_URL =
  "https://calendly.com/d/cvxs-349-5w3/companion-askcompanion-com";
const APP_URL = "https://app.scribenote.com/dashboard?companion";

const baseLink = "text-sm font-medium text-secondary no-underline";
const activeLink = "text-sm font-semibold text-indigo-700 no-underline";

const bookDemoBase =
  "rounded-full border border-indigo-200 px-5 py-2.5 font-body text-sm font-semibold text-indigo-700 no-underline transition-[background,transform] duration-150 ease-[ease] hover:bg-indigo-50";
const getStartedBase =
  "primary cursor-pointer rounded-full border-0 bg-indigo-500 px-5 py-2.5 font-body text-sm font-semibold text-white no-underline shadow-[0_6px_16px_-6px_rgba(99,102,241,0.55)] transition-[transform,box-shadow,background] duration-150 ease-[ease] hover:bg-indigo-600 hover:shadow-[0_10px_20px_-6px_rgba(99,102,241,0.65)]";

export function MarketingNav({ current }: MarketingNavProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 960px)");
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setMenuOpen(false);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <nav className="relative mx-auto flex max-w-[1400px] items-center justify-between px-[clamp(24px,5vw,80px)] py-5">
      <Link to="/" className="flex items-center no-underline" aria-label="Home">
        <Wordmark size={51} />
      </Link>

      {/* Desktop nav (≥ 960px) */}
      <div className="hidden items-center gap-7 min-[960px]:flex">
        <Link to="/" className={current === "home" ? activeLink : baseLink}>
          Home
        </Link>
        <Link
          to="/pricing"
          className={current === "pricing" ? activeLink : baseLink}
        >
          Pricing
        </Link>
        <Link
          to="/use-cases"
          className={current === "use-cases" ? activeLink : baseLink}
        >
          Use cases
        </Link>
        <Link to="/blog" className={current === "blog" ? activeLink : baseLink}>
          Blog
        </Link>
        <div className="flex items-center gap-2.5 pl-2">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`${bookDemoBase} hover:-translate-y-px`}
          >
            Book a demo
          </a>
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`${getStartedBase} hover:-translate-y-px`}
          >
            Get started
          </a>
        </div>
      </div>

      {/* Mobile hamburger trigger (< 960px) */}
      <button
        type="button"
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-expanded={menuOpen}
        aria-controls="mkt-mobile-menu"
        onClick={() => setMenuOpen((v) => !v)}
        className="grid h-11 w-11 place-items-center rounded-full border border-grindigo-100 bg-white text-indigo-700 shadow-[0_2px_6px_-2px_rgba(99,102,241,0.25)] transition-[background] duration-150 ease-[ease] hover:bg-indigo-50 min-[960px]:hidden"
      >
        {menuOpen ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 6h16M4 12h16M4 18h16"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        )}
      </button>

      {/* Mobile menu sheet (< 960px) */}
      {menuOpen && (
        <div
          id="mkt-mobile-menu"
          className="absolute top-full right-[clamp(24px,5vw,80px)] z-20 mt-2 flex w-[min(280px,calc(100vw-48px))] flex-col gap-1 rounded-2xl border border-grindigo-100 bg-white p-3 shadow-[0_30px_60px_-20px_rgba(30,27,75,0.35)] min-[960px]:hidden"
        >
          <Link
            to="/"
            onClick={closeMenu}
            className={`rounded-xl px-3 py-2.5 text-[15px] no-underline hover:bg-indigo-50 hover:text-indigo-700 ${
              current === "home"
                ? "font-semibold text-indigo-700"
                : "font-medium text-secondary"
            }`}
          >
            Home
          </Link>
          <Link
            to="/pricing"
            onClick={closeMenu}
            className={`rounded-xl px-3 py-2.5 text-[15px] no-underline hover:bg-indigo-50 hover:text-indigo-700 ${
              current === "pricing"
                ? "font-semibold text-indigo-700"
                : "font-medium text-secondary"
            }`}
          >
            Pricing
          </Link>
          <Link
            to="/use-cases"
            onClick={closeMenu}
            className={`rounded-xl px-3 py-2.5 text-[15px] no-underline hover:bg-indigo-50 hover:text-indigo-700 ${
              current === "use-cases"
                ? "font-semibold text-indigo-700"
                : "font-medium text-secondary"
            }`}
          >
            Use cases
          </Link>
          <Link
            to="/blog"
            onClick={closeMenu}
            className={`rounded-xl px-3 py-2.5 text-[15px] no-underline hover:bg-indigo-50 hover:text-indigo-700 ${
              current === "blog"
                ? "font-semibold text-indigo-700"
                : "font-medium text-secondary"
            }`}
          >
            Blog
          </Link>
          <div className="my-2 h-px bg-grindigo-100" aria-hidden="true" />
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
            className={`${bookDemoBase} text-center`}
          >
            Book a demo
          </a>
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
            className={`${getStartedBase} w-full text-center`}
          >
            Get started
          </a>
        </div>
      )}
    </nav>
  );
}
